import React, { useEffect, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import useCoupons from 'api/useCoupons';
import useOffers from 'api/useOffers';
import { Coupon, EPublisherSolutionModel, Offer } from 'common/contracts';
import { DATE_TIME_FORMAT } from 'constants/constants';
import { ELocalStorageKeys, OfferType } from 'constants/enums';
import dayjs from 'dayjs';
import { FormikErrors, FormikTouched, useFormik } from 'formik';
import { useUnsavedChanges } from 'hooks/useUnsavedChanges';
import { localStorageUtil } from 'utils/localStorageUtil';

import PortalHeader from 'components/PortalHeader/PortalHeader';
import RichText from 'components/RichText/RichText';

import { EToasterStatus, showToast } from '../../../utils/showToast';
import { ICouponsFormProps, UpsertCouponWithPromoCodeDto } from '../types';
import {
  buildCouponValidationSchema,
  checkUniqueness,
  deriveExpirationMode,
  generateCouponDto,
  generatePromoCodesDto,
  getOffersForSelect
} from '../utils';

import CouponsGeneralSettings from './components/CouponsGeneralSettings';
import CouponsOffersSettings from './components/CouponsOffersSettings';
import CouponsPlayerSettings from './components/CouponsPlayersSettings/CouponsPlayerSettings';
import CouponsPromoCodes from './components/CouponsPromoCodes';

import '../../../style/forms.scss';

export const CouponsForm: React.FC<ICouponsFormProps> = ({
  edit = false,
  dup = false
}) => {
  const { publisherId } = useParams();
  const navigate = useNavigate();
  const { couponId } = useParams();

  const { getSingleCoupon, upsertCoupon, isPromoCodeActive } = useCoupons(
    publisherId,
    couponId
  );
  const { getCoupons } = useCoupons(publisherId);

  const initialStartsAt = (getSingleCoupon.data as any)?.startsAt || null;
  const initialExpiredBy = getSingleCoupon.data?.expiredBy || null;
  const initialExpirationMode = deriveExpirationMode(
    initialStartsAt,
    initialExpiredBy
  );

  const [neverExpires, setNeverExpires] = useState<boolean>(false);

  const [promoCode, setPromoCode] = useState('');
  const [maxRedemptions, setMaxRedemptions] = useState<string | number>('');

  const { fetchAllOffers } = useOffers({});

  const [offersMap, setOffersMap] = useState<{
    bundles: Offer[];
    promotions: Offer[];
    popups: Offer[];
  }>({
    bundles: [],
    promotions: [],
    popups: []
  });

  const [isLoadingOffers, setIsLoadingOffers] = useState(true);

  useEffect(() => {
    const fetchAllOffersData = async () => {
      try {
        setIsLoadingOffers(true);

        const [bundleOffers, specialOffers, popupOffers] = await Promise.all([
          fetchAllOffers({
            offerType: OfferType.BUNDLE,
            fetchOnlyActiveOffers: true
          }),
          fetchAllOffers({
            offerType: OfferType.SPECIAL_OFFER,
            fetchOnlyActiveOffers: true
          }),
          fetchAllOffers({
            offerType: OfferType.POPUP,
            fetchOnlyActiveOffers: true
          })
        ]);

        setOffersMap({
          bundles: bundleOffers,
          promotions: specialOffers,
          popups: popupOffers
        });
      } catch (error) {
        console.error('Error fetching all offers:', error);
        showToast({
          message: 'Failed to load offers',
          status: EToasterStatus.ERROR
        });
      } finally {
        setIsLoadingOffers(false);
      }
    };

    if (publisherId) {
      fetchAllOffersData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publisherId]);

  const isCheckoutUser =
    localStorageUtil.get(ELocalStorageKeys.PUBLISHER_SOLUTION_MODEL) ===
    EPublisherSolutionModel.CHECKOUT;

  const supportedOffersBySku = isCheckoutUser
    ? []
    : getSingleCoupon.data?.supportedOffersBySku ??
      (isLoadingOffers
        ? []
        : getOffersForSelect(
            offersMap,
            getSingleCoupon.data?.supportedOffersByType || []
          )?.map((offer) => offer.key));

  let couponSchema = buildCouponValidationSchema({
    checkUniqueness,
    getCouponsData: getCoupons.data,
    currentCouponName: getSingleCoupon.data?.name || '',
    isEdit: Boolean(edit),
    isCheckoutUser
  });

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    errors,
    dirty,
    isValid,
    touched,
    setFieldValue,
    setFieldTouched
  } = useFormik({
    validateOnMount: true,
    validationSchema: couponSchema,
    enableReinitialize: true,
    initialValues: {
      name: getSingleCoupon.data?.name || '',
      active: getSingleCoupon.data?.active ?? true,
      discountPercentage: getSingleCoupon.data?.discountPercentage || '',
      maxRedemptionsPerCustomer:
        getSingleCoupon.data?.maxRedemptionsPerCustomer || '',
      expiredBy: initialExpiredBy,
      startsAt: initialStartsAt as string | null,
      expirationMode: initialExpirationMode,
      firstTimePurchase: getSingleCoupon.data?.firstTimePurchase || false,
      supportedOffersBySku,
      promoCodes: getSingleCoupon.data?.promoCodes || [],
      allowedPlayers: getSingleCoupon.data?.allowedPlayers || []
    },
    onSubmit: async (values, helpers) => {
      const promoCodesData = generatePromoCodesDto(values.promoCodes, couponId);
      const couponData = generateCouponDto(values, neverExpires);

      const upsertCouponData: UpsertCouponWithPromoCodeDto = {
        coupon: couponData,
        promoCodes: promoCodesData
      };

      const isEdit = Boolean(edit);

      const onSuccess = async () => {
        if (edit) {
          await getSingleCoupon.refetch();
        }
        if (getSingleCoupon.data) {
          helpers.setValues({
            ...values,
            ...getSingleCoupon.data,
            promoCodes: (getSingleCoupon.data.promoCodes || []).map(
              (promoCode) => ({
                ...promoCode,
                fresh: false
              })
            ),
            startsAt: (getSingleCoupon.data?.startsAt ?? null) as string | null,
            expirationMode: deriveExpirationMode(
              getSingleCoupon.data?.startsAt,
              getSingleCoupon.data?.expiredBy
            )
          });
        }
        showToast({
          message: `Coupon ${isEdit ? 'updated' : 'created'} successfully`,
          status: EToasterStatus.SUCCESS
        });

        if (!isEdit) {
          navigate(-1);
        }
      };

      const onError = (error: any) => {
        const serverError = error?.response?.data?.message;
        showToast({
          message:
            serverError || `Error ${isEdit ? 'updating' : 'creating'} coupon`,
          status: EToasterStatus.ERROR
        });
      };

      upsertCoupon.mutate(upsertCouponData, {
        onSuccess,
        onError
      });
    }
  });

  useUnsavedChanges({ dirty });

  const getOffersForSelectBound = (supportedOffersByType?: string[]) =>
    getOffersForSelect(offersMap, supportedOffersByType);

  const formTitle = edit ? 'Edit Coupon' : 'Create Coupon';

  return (
    <div className="overflow-scroll">
      <PortalHeader
        title={formTitle}
        primaryButtonText="Save"
        onPrimaryButtonClick={handleSubmit}
        primaryButtonState={{
          disabled: !dirty || !isValid,
          isLoading: upsertCoupon.isLoading
        }}
      />
      <div className="w-full px-7 pb-20">
        <div className="border border-base-menuBorder rounded-[20px] mb-5 p-9">
          <div className="flex justify-between items-start mb-[46px]">
            <RichText
              title="General Settings"
              description="Configure the main properties of your coupon, such as identification, discount rules, and availability."
            />
            {getSingleCoupon.data?.updatedAt && (
              <span className="text-xs text-green-600 whitespace-nowrap">
                Last edited {dayjs(getSingleCoupon.data.updatedAt).format(DATE_TIME_FORMAT)}
              </span>
            )}
          </div>
          <CouponsGeneralSettings
            couponFormValues={values}
            edit={edit}
            handleChange={handleChange}
            handleBlur={handleBlur}
            errors={errors as FormikErrors<Coupon>}
            touched={touched as FormikTouched<Coupon>}
            setFieldTouched={setFieldTouched}
            setFieldValue={setFieldValue}
            neverExpires={neverExpires}
            dup={dup}
            setNeverExpires={setNeverExpires}
          />
        </div>

        {!isCheckoutUser && (
          <div className="border border-base-menuBorder rounded-[20px] mb-5 p-9">
            <RichText
              title="Offers Settings"
              description="Apply this coupon to selected offers or all offers."
             className="mb-[33px]"
            />
            <CouponsOffersSettings
              supportedOffersBySku={values.supportedOffersBySku}
              getOffersForSelect={getOffersForSelectBound}
              handleChange={handleChange}
              handleBlur={handleBlur}
              errors={errors as FormikErrors<Coupon>}
              touched={touched as FormikTouched<Coupon>}
              setFieldValue={setFieldValue}
            />
          </div>
        )}

        <div className="border border-base-menuBorder rounded-[20px] mb-5 p-9">
          <RichText
            title="Promo Codes"
            description="Create one or more promo codes your players can use to redeem this coupon."
            className="mb-[46px]"
          />
          <CouponsPromoCodes
            couponFormValues={values}
            handleBlur={handleBlur}
            setFieldValue={setFieldValue}
            promoCode={promoCode}
            setPromoCode={setPromoCode}
            maxRedemptions={maxRedemptions}
            setMaxRedemptions={setMaxRedemptions}
            isPromoCodeActive={isPromoCodeActive}
          />
        </div>

        <div className="border border-base-menuBorder rounded-[20px] mb-5 p-9">
          <RichText
            title="Player Settings"
            description="Set rules for who can use this coupon, such as first-time buyers, redemption limits, or specific player access."
            className="mb-5"
          />
          <CouponsPlayerSettings
            couponFormValues={values}
            handleChange={handleChange}
            handleBlur={handleBlur}
            errors={errors as FormikErrors<Coupon>}
            touched={touched as FormikTouched<Coupon>}
            setFieldValue={setFieldValue}
            dup={dup}
          />
        </div>
      </div>
    </div>
  );
};
